/**
 * @deprecated Use GameAccordion instead. Kept as LegacyCombinedOrdersPositionsPanel for rollback.
 *
 * CombinedOrdersPositionsPanel - Unified view of orders and positions
 *
 * Prototype panel that combines open orders and filled positions into a single
 * table, grouped by (ticker, side).
 */

import { useMemo, useState, useCallback } from 'react';
import { cn } from '@/lib/infra/utils';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronUp, X } from 'lucide-react';
import type { KalshiOrder } from '@/lib/kalshiApi';
import type { Position } from '@/types';
import { formatPriceCents } from '@/lib/nbaConsolidated/format';
import {
  derivePositionLabel,
  deriveGameDescription,
  deriveDateFromTicker,
} from '@/lib/nbaConsolidated/positionLabel';
import { isAtTopOfBook, type TickerOrderbook } from '@/lib/nbaConsolidated/topOfBook';
import { computeCost } from '@/lib/trading/orderMath';
import { TickerDisplay } from '@/components/atoms/TickerDisplay';
import { Money } from '@/components/atoms/Money';
import { DateDisplay } from '@/components/atoms/DateDisplay';
import { Th, Td } from '@/components/atoms/TableCells';
import { ConfirmModal } from '@/components/atoms/Modal';

interface CombinedOrdersPositionsPanelProps {
  kalshiOrders: KalshiOrder[];
  positions: Position[];
  /** Optional mapping from ticker -> market title */
  titleByTicker?: Record<string, string>;
  /** Optional mapping from ticker -> game date (ISO string) */
  gameDateByTicker?: Record<string, string>;
  /** Optional mapping from ticker -> { yes/no outcome label } */
  outcomeByTicker?: Record<string, { yes?: string; no?: string }>;
  /** Optional mapping from ticker -> orderbook best bid/ask */
  orderbookByTicker?: Record<string, TickerOrderbook>;
  /** Handler for canceling an order */
  onCancelOrder?: (orderId: string) => void;
  /** Handler for killing all orders */
  onKillAll?: () => void;
  className?: string;
}

interface UnifiedRow {
  // Grouping key
  key: string;
  ticker: string;
  side: 'yes' | 'no';

  // Display fields
  gameDate: string | null;
  gameDescription: string;
  positionLabel: string;

  // Order fields
  orderId: string | null;
  orderAction: 'buy' | 'sell' | null;
  qtyOpen: number;
  queuePosition: number | null;
  orderPriceCents: number | null;
  /** Whether order is at top of book (best price). null if no order or no orderbook data */
  topOfBook: boolean | null;

  // USD cost fields
  orderCostDollars: number | null;
  positionCostDollars: number | null;

  // Position fields
  qtyClosed: number;
  avgPriceCents: number | null;
  currentPriceCents: number | null;
  pnl: number | null;
}

type CombinedSortKey =
  | 'date'
  | 'market'
  | 'qtyOpen'
  | 'qtyClosed'
  | 'cost'
  | 'queue'
  | 'avg'
  | 'current'
  | 'pnl';
type SortDir = 'asc' | 'desc';

function getSortValue(row: UnifiedRow, key: CombinedSortKey): string | number {
  switch (key) {
    case 'date':
      return row.gameDate ?? '';
    case 'market':
      return row.gameDescription;
    case 'qtyOpen':
      return row.qtyOpen;
    case 'qtyClosed':
      return row.qtyClosed;
    case 'cost':
      return row.orderCostDollars ?? row.positionCostDollars ?? 0;
    case 'queue':
      return row.queuePosition ?? Infinity;
    case 'avg':
      return row.avgPriceCents ?? row.orderPriceCents ?? 0;
    case 'current':
      return row.currentPriceCents ?? 0;
    case 'pnl':
      return row.pnl ?? 0;
    default:
      return 0;
  }
}

function CombinedSortableHeader({
  label,
  sortKey,
  currentKey,
  currentDir,
  onSort,
  className,
}: {
  label: string;
  sortKey: string;
  currentKey: string | null;
  currentDir: SortDir;
  onSort: (key: string) => void;
  className?: string;
}) {
  const isActive = currentKey === sortKey;
  return (
    <Th className={cn('cursor-pointer select-none', className)}>
      <button
        className="hover:text-foreground inline-flex items-center gap-1 transition-colors"
        onClick={() => onSort(sortKey)}
      >
        {label}
        {isActive ? (
          currentDir === 'asc' ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </button>
    </Th>
  );
}

export function LegacyCombinedOrdersPositionsPanel({
  kalshiOrders,
  positions,
  titleByTicker,
  gameDateByTicker,
  outcomeByTicker,
  orderbookByTicker,
  onCancelOrder,
  onKillAll,
  className,
}: CombinedOrdersPositionsPanelProps) {
  const rawRows = useMemo(() => {
    const rowMap = new Map<string, UnifiedRow>();

    // Process orders first
    for (const order of kalshiOrders) {
      const key = `${order.ticker}:${order.side}`;
      const existing = rowMap.get(key);

      const priceCents = order.side === 'yes' ? order.yes_price : order.no_price;
      const tickerDate = deriveDateFromTicker(order.ticker);
      const gameDate = gameDateByTicker?.[order.ticker] || tickerDate || undefined;

      // Calculate top of book status
      const topOfBook =
        priceCents !== null && priceCents !== undefined
          ? isAtTopOfBook({
              action: order.action,
              side: order.side,
              orderPriceCents: priceCents,
              orderbook: orderbookByTicker?.[order.ticker],
            })
          : null;

      // Compute USD cost for this order
      const orderCostDollars =
        priceCents != null
          ? computeCost(priceCents, order.remaining_count, order.side, 'kalshi', false).costDollars
          : null;

      if (existing) {
        // Merge order into existing row
        existing.orderId = order.order_id;
        existing.orderAction = order.action;
        existing.qtyOpen = order.remaining_count;
        existing.queuePosition = order.queue_position ?? null;
        existing.orderPriceCents = priceCents ?? null;
        existing.topOfBook = topOfBook;
        existing.orderCostDollars = orderCostDollars;
      } else {
        rowMap.set(key, {
          key,
          ticker: order.ticker,
          side: order.side,
          gameDate: gameDate ?? null,
          gameDescription: deriveGameDescription(order.ticker, titleByTicker?.[order.ticker]),
          positionLabel: derivePositionLabel(order.ticker, order.side, outcomeByTicker),
          orderId: order.order_id,
          orderAction: order.action,
          qtyOpen: order.remaining_count,
          queuePosition: order.queue_position ?? null,
          orderPriceCents: priceCents ?? null,
          topOfBook,
          orderCostDollars,
          positionCostDollars: null,
          qtyClosed: 0,
          avgPriceCents: null,
          currentPriceCents: null,
          pnl: null,
        });
      }
    }

    // Process positions
    for (const pos of positions) {
      const key = `${pos.ticker}:${pos.side}`;
      const existing = rowMap.get(key);

      const tickerDate = deriveDateFromTicker(pos.ticker);
      const gameDate = gameDateByTicker?.[pos.ticker] || tickerDate || undefined;

      // Convert position price from dollars to cents
      const avgCents = pos.averagePrice * 100;
      const currentCents = pos.currentPrice !== null ? pos.currentPrice * 100 : null;

      // Compute position cost: quantity × avgPrice
      const posCostDollars = pos.averagePrice * pos.quantity;

      if (existing) {
        // Merge position into existing row
        existing.qtyClosed = pos.quantity;
        existing.avgPriceCents = avgCents;
        existing.currentPriceCents = currentCents;
        existing.pnl = pos.totalPnl;
        existing.positionCostDollars = posCostDollars;
      } else {
        rowMap.set(key, {
          key,
          ticker: pos.ticker,
          side: pos.side,
          gameDate: gameDate ?? null,
          gameDescription: deriveGameDescription(pos.ticker, titleByTicker?.[pos.ticker]),
          positionLabel: derivePositionLabel(pos.ticker, pos.side, outcomeByTicker),
          orderId: null,
          orderAction: null,
          qtyOpen: 0,
          queuePosition: null,
          orderPriceCents: null,
          topOfBook: null,
          orderCostDollars: null,
          positionCostDollars: posCostDollars,
          qtyClosed: pos.quantity,
          avgPriceCents: avgCents,
          currentPriceCents: currentCents,
          pnl: pos.totalPnl,
        });
      }
    }

    return Array.from(rowMap.values());
  }, [
    kalshiOrders,
    positions,
    titleByTicker,
    gameDateByTicker,
    outcomeByTicker,
    orderbookByTicker,
  ]);

  const [sortKey, setSortKey] = useState<CombinedSortKey | null>('date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = useCallback(
    (key: string) => {
      const k = key as CombinedSortKey;
      if (sortKey === k) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(k);
        setSortDir(k === 'pnl' || k === 'queue' ? 'asc' : 'desc');
      }
    },
    [sortKey]
  );

  const sortedRows = useMemo(() => {
    const rows = [...rawRows];
    if (!sortKey) return rows;
    rows.sort((a, b) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);
      const cmp =
        typeof av === 'string' ? av.localeCompare(bv as string) : (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [rawRows, sortKey, sortDir]);

  const totalPnl = useMemo(() => {
    return rawRows.reduce((sum, row) => sum + (row.pnl ?? 0), 0);
  }, [rawRows]);

  const [showAllRows, setShowAllRows] = useState(false);
  const [showKillAllConfirm, setShowKillAllConfirm] = useState(false);
  const maxRows = 10;

  const visibleRows = useMemo(() => {
    if (showAllRows) return sortedRows;
    return sortedRows.slice(0, maxRows);
  }, [sortedRows, showAllRows]);

  const hasOpenOrders = kalshiOrders.length > 0;

  return (
    <div className={cn('bg-card border-border rounded-lg border', className)}>
      {/* Header - clean section title */}
      <div className="border-border flex items-center justify-between border-b px-4 py-4">
        <div className="flex items-center gap-3">
          <h2 className="text-foreground text-lg font-semibold">Orders & Positions</h2>
          <span className="text-muted-foreground text-xs">{sortedRows.length} rows</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="font-mono text-sm font-semibold">
            Total P&L:{' '}
            <Money
              value={totalPnl}
              variant="pnl"
            />
          </div>
          {hasOpenOrders && onKillAll && (
            <button
              onClick={() => setShowKillAllConfirm(true)}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-md px-4 py-2 text-xs font-medium uppercase tracking-wider shadow-sm transition-colors"
            >
              Kill All
            </button>
          )}

          {/* Kill All Confirmation Modal */}
          <ConfirmModal
            isOpen={showKillAllConfirm}
            onClose={() => setShowKillAllConfirm(false)}
            onConfirm={() => onKillAll?.()}
            title="Cancel All Orders"
            message={`Are you sure you want to cancel all ${kalshiOrders.length} open order${kalshiOrders.length === 1 ? '' : 's'}? This action cannot be undone.`}
            confirmLabel="Kill All"
            cancelLabel="Keep Orders"
            variant="danger"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <CombinedSortableHeader
                label="Date"
                sortKey="date"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
              <CombinedSortableHeader
                label="Market"
                sortKey="market"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
              <Th>Position</Th>
              <CombinedSortableHeader
                label="Qty Open"
                sortKey="qtyOpen"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
                className="text-right"
              />
              <CombinedSortableHeader
                label="Qty Closed"
                sortKey="qtyClosed"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
                className="text-right"
              />
              <CombinedSortableHeader
                label="Cost"
                sortKey="cost"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
                className="text-right"
              />
              <CombinedSortableHeader
                label="Queue"
                sortKey="queue"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
                className="text-right"
              />
              <Th className="text-center">Top</Th>
              <CombinedSortableHeader
                label="Avg"
                sortKey="avg"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
                className="text-right"
              />
              <CombinedSortableHeader
                label="Current"
                sortKey="current"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
                className="text-right"
              />
              <CombinedSortableHeader
                label="P&L"
                sortKey="pnl"
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
                className="text-right"
              />
              <Th className="w-10 text-center"> </Th>
            </tr>
          </thead>
          <tbody className="divide-border divide-y">
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={12}
                  className="text-muted-foreground px-3 py-6 text-center"
                >
                  No orders or positions
                </td>
              </tr>
            ) : (
              visibleRows.map((row, rowIndex) => {
                // Determine which price to show in Avg column
                // If we have a position, show avg fill price; otherwise show order price
                const avgToShow = row.avgPriceCents ?? row.orderPriceCents;
                const zebraStyle =
                  rowIndex % 2 === 1
                    ? { backgroundColor: 'hsl(var(--muted) / var(--zebra-opacity))' }
                    : undefined;

                return (
                  <tr
                    key={row.key}
                    className="hover:bg-accent/50 transition-colors"
                    style={zebraStyle}
                  >
                    <Td className="font-mono text-xs">
                      <DateDisplay value={row.gameDate} />
                    </Td>
                    <Td>
                      <TickerDisplay
                        ticker={row.ticker}
                        title={row.gameDescription}
                        variant="compact"
                      />
                    </Td>
                    <Td>
                      <span
                        className={cn(
                          'rounded px-2 py-0.5 text-xs font-medium',
                          row.side === 'yes'
                            ? 'bg-positive/20 text-positive'
                            : 'bg-negative/20 text-negative'
                        )}
                      >
                        {row.positionLabel}
                      </span>
                    </Td>
                    <Td className="text-right font-mono">
                      {row.qtyOpen > 0 ? row.qtyOpen.toLocaleString() : '—'}
                    </Td>
                    <Td className="text-right font-mono">
                      {row.qtyClosed > 0 ? row.qtyClosed.toLocaleString() : '—'}
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      <Money
                        value={row.orderCostDollars ?? row.positionCostDollars}
                        className="text-xs"
                      />
                    </Td>
                    <Td className="text-muted-foreground text-right font-mono">
                      {row.queuePosition !== null ? row.queuePosition.toLocaleString() : '—'}
                    </Td>
                    <Td className="text-center">
                      {row.topOfBook === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : row.topOfBook ? (
                        <span className="text-positive">✓</span>
                      ) : (
                        <span className="text-muted-foreground">✗</span>
                      )}
                    </Td>
                    <Td className="text-right font-mono">
                      {avgToShow !== null ? formatPriceCents(avgToShow) : '—'}
                    </Td>
                    <Td className="text-right font-mono">
                      {row.currentPriceCents !== null
                        ? formatPriceCents(row.currentPriceCents)
                        : '—'}
                    </Td>
                    <Td className="text-right font-mono font-semibold">
                      <Money
                        value={row.pnl}
                        variant="pnl"
                      />
                    </Td>
                    <Td className="text-center">
                      {row.orderId && onCancelOrder && (
                        <button
                          onClick={() => onCancelOrder(row.orderId!)}
                          className="hover:bg-destructive/20 hover:text-destructive rounded p-1 transition-colors"
                          title="Cancel order"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Show more/less */}
      {sortedRows.length > maxRows && (
        <div className="border-border/50 flex items-center justify-center border-t p-2">
          <button
            type="button"
            onClick={() => setShowAllRows((v) => !v)}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/30 flex items-center gap-2 rounded px-3 py-1.5 text-xs transition-colors"
            aria-expanded={showAllRows}
            aria-label={showAllRows ? 'Show fewer rows' : 'Show more rows'}
          >
            <span className="tabular-nums">
              {showAllRows
                ? `Showing ${sortedRows.length}`
                : `Showing ${Math.min(maxRows, sortedRows.length)} of ${sortedRows.length}`}
            </span>
            {showAllRows ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      )}
    </div>
  );
}

export default LegacyCombinedOrdersPositionsPanel;
